import React, { useState, useEffect } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { t } from 'i18next';
import {
  GroupUserI,
  UserPageInterfaceI,
  paramsI
} from '../../../types/interface';
import { useAppDispatch } from '../../redux/HooksRedux';
import * as userAction from '../../redux/user/userAction';
import { ViewAllLimit } from 'utils/enums/misc';
import SearchBox from 'components/common/SearchBox';
import { DataNotFound } from 'base/DataNotFound/DataNotFound';
import { StandardButton } from 'base/StandardButton/StandardButton';
import { StandardLoader } from 'base/Loader/Loader';
import ConditionalRender from 'base/ConditionalRender/ConditionalRender';
import { UilUsersAlt } from '@iconscout/react-unicons';
import 'components/ViewUserModal/ViewUserModal.scss';

interface UserSelectModalProps {
  show: boolean;
  onHide: () => void;
  selected: GroupUserI[];
  onConfirm: (selected: GroupUserI[]) => void;
}

const UserSelectModal = ({
  show,
  onHide,
  selected,
  onConfirm
}: UserSelectModalProps) => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState<GroupUserI[]>([]);
  const [searchText, setSearchText] = useState('');
  const [localSelection, setLocalSelection] = useState<GroupUserI[]>(selected);

  useEffect(() => {
    if (show) {
      setLocalSelection(selected);
      setSearchText('');
      fetchItems();
    }
  }, [show]);

  const fetchItems = async () => {
    setIsLoading(true);
    const params: paramsI = {
      page: 1,
      limit: ViewAllLimit,
      keyword: '',
      account_type: 'ADMIN'
    };
    try {
      const response = await dispatch(userAction.getAllUsers(params));
      if (response?.status === 200) {
        const data: UserPageInterfaceI[] = response.data?.data || [];
        setItems(
          data.map(u => ({
            user_id: u.user_id,
            first_name: u.first_name,
            last_name: u.last_name,
            email: u.email
          }))
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const filteredItems = items.filter(u => {
    const fullName = `${u.first_name} ${u.last_name}`.toLowerCase();
    const search = searchText.toLowerCase();
    return fullName.includes(search) || u.email?.toLowerCase().includes(search);
  });

  const isChecked = (userId: number) =>
    localSelection.some(u => u.user_id === userId);

  const toggleItem = (item: GroupUserI) => {
    setLocalSelection(prev =>
      isChecked(item.user_id)
        ? prev.filter(u => u.user_id !== item.user_id)
        : [...prev, item]
    );
  };

  const handleConfirm = () => {
    onConfirm(localSelection);
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      className="view-admin-level-modal user-select-modal"
    >
      <Modal.Header closeButton>
        <div className="level-header-content">
          <div className="level-avatar">
            <UilUsersAlt size={22} color="#dc3545" />
          </div>
          <div>
            <p className="level-title">{t('group_management.form.text_5')}</p>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        <ConditionalRender condition={!isLoading} fallBack={<StandardLoader />}>
          <div className="search-bar-sticky d-flex justify-content-between align-items-center">
            <div className="users-summary">
              <UilUsersAlt size={22} color="currentColor" />
              {localSelection.length} {t('general.text_15')}
            </div>
            <SearchBox
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
            />
          </div>
          {filteredItems.length > 0 ? (
            <div className="users-grid">
              {filteredItems.map(item => (
                <div
                  className="user-card"
                  key={`select-user-${item.user_id}`}
                  onClick={() => toggleItem(item)}
                  style={{ cursor: 'pointer' }}
                >
                  <Form.Check
                    type="checkbox"
                    checked={isChecked(item.user_id)}
                    onChange={() => toggleItem(item)}
                    onClick={e => e.stopPropagation()}
                  />
                  <div className="user-info">
                    <span className="user-name">
                      {item.first_name} {item.last_name}
                    </span>
                    <span className="user-email">{item.email}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <DataNotFound />
          )}
        </ConditionalRender>
      </Modal.Body>

      <Modal.Footer>
        <StandardButton
          variant="outline-danger"
          buttonText={t('general.common_text.text_15')}
          onClick={onHide}
        />
        <StandardButton
          buttonText={t('general.common_text.text_2')}
          onClick={handleConfirm}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default UserSelectModal;
